import { useCallback, useEffect, useRef, useState } from "react";
import type { EmbedProductDto, EmbedViewerDto } from "../../types/tree";
import {
  ConfiguratorViewer,
  type ConfiguratorViewerHandle,
} from "../../components/ConfiguratorViewer";

/**
 * Bare full-window Unity viewer tool, hosted in an iframe.
 *
 * Why an iframe: Unity's WebGL framework only resizes its drawing buffer when it
 * detects size changes through its own internal hooks. Inside a Shadow DOM
 * (which the embed loader uses for style isolation), those hooks miss layout
 * changes from ancestor elements going fullscreen, and the buffer stays at boot
 * dimensions while the canvas's CSS size grows — producing the well-known
 * stuck-in-the-corner / blurry render. Loading Unity inside an iframe gives it
 * a clean top-level `document` where its native resize behaviour just works.
 *
 * The iframe is fully driven by the parent over postMessage — it boots empty
 * and waits for `rt:setViewer` (Unity build info) before mounting Unity, then
 * forwards `rt:setProduct` / `rt:setMaterials` to the running scene as
 * LoadProduct / SetSurfaceMaterials messages. The iframe itself does no API
 * calls; the parent already has the bundle and ships the bits it needs.
 *
 * Handshake: as soon as the iframe is alive it posts `rt:ready` upward so the
 * parent knows it can start sending messages.
 */
export interface RealtimeViewerToolProps {
  /** API base URL Unity uses for asset fetches. Defaults to window.location.origin. */
  apiBaseUrl?: string;
  /** Origin to load configurator-viewer.js from. Empty = same origin. */
  baseUrl?: string;
}

// PascalCase to match Unity's MaterialBinding fields (JsonUtility is
// case-sensitive). Same shape as the spawn-bundle's defaultBindings.
interface SurfaceBinding {
  Key: string;
  DmatId: string;
}

function buildBindings(product: EmbedProductDto, materialPicks: Record<string, string>): SurfaceBinding[] {
  const out: SurfaceBinding[] = [];
  for (const s of product.surfaces) {
    const pickedMaterialId = materialPicks[s.surfaceKey] ?? s.defaultMaterialId;
    if (!pickedMaterialId) continue;
    const picked = s.materials.find(m => m.id === pickedMaterialId);
    if (!picked?.dmatAssetId) continue;
    out.push({ Key: s.surfaceKey, DmatId: picked.dmatAssetId });
  }
  return out;
}

/**
 * postMessage envelope from parent → iframe. Discriminated by `type`. Anything
 * else is silently dropped so unrelated host-page messages don't confuse us.
 */
type ParentMessage =
  | { type: "rt:setViewer"; viewer: EmbedViewerDto }
  | { type: "rt:setProduct"; product: EmbedProductDto }
  | { type: "rt:setMaterials"; materialPicks: Record<string, string> }
  | { type: "rt:setBackground"; hex: string };

export function RealtimeViewerTool(props: RealtimeViewerToolProps) {
  const [viewer, setViewer] = useState<EmbedViewerDto | null>(null);
  const [product, setProduct] = useState<EmbedProductDto | null>(null);
  const [materialPicks, setMaterialPicks] = useState<Record<string, string>>({});
  const [unityError, setUnityError] = useState<string | null>(null);
  // Background colour for both the iframe body AND Unity's camera clear colour.
  // Defaults to the dashboard's image-stage background so the realtime view
  // reads as the same surface as the rendered media — a parent rt:setBackground
  // message can re-skin to match a custom theme.
  const [backgroundHex, setBackgroundHex] = useState<string>("#f3f3f3");

  const viewerRef = useRef<ConfiguratorViewerHandle | null>(null);
  const [viewerReady, setViewerReady] = useState(false);

  // ── postMessage IPC from parent ─────────────────────────────────────
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const msg = e.data as ParentMessage | undefined;
      if (!msg || typeof msg !== "object" || typeof msg.type !== "string") return;
      if (msg.type === "rt:setViewer") {
        setViewer(msg.viewer);
        return;
      }
      if (msg.type === "rt:setProduct") {
        setProduct(msg.product);
        return;
      }
      if (msg.type === "rt:setMaterials") {
        setMaterialPicks(msg.materialPicks ?? {});
        return;
      }
      if (msg.type === "rt:setBackground") {
        if (typeof msg.hex === "string" && msg.hex.length > 0) {
          setBackgroundHex(msg.hex);
        }
        return;
      }
    };
    window.addEventListener("message", handler);
    // Announce readiness so the parent knows it can start sending messages.
    // window.parent is always present in an iframe; the wildcard targetOrigin
    // is fine since we're only signalling presence with no sensitive payload.
    try { window.parent?.postMessage({ type: "rt:ready" }, "*"); } catch { /* ignore */ }
    return () => window.removeEventListener("message", handler);
  }, []);

  // ── Document title tracks the product name ────────────────────────
  // The iframe browsing-context title is what the user sees in tab labels
  // and what screen readers announce; keep it in sync with whichever product
  // the parent has loaded so it's not "Viewer Preview" forever.
  useEffect(() => {
    if (product?.name) {
      document.title = product.name;
    }
  }, [product?.name]);

  // ── Push to Unity ───────────────────────────────────────────────────
  const lastSentProductIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!viewerReady) return;
    const v = viewerRef.current;
    if (!v || !product) return;
    if (lastSentProductIdRef.current === product.id) return;

    const bindings = buildBindings(product, materialPicks);
    const payload = JSON.stringify({ dmodelAssetId: product.modelAssetId, bindings });
    setUnityError(null);
    lastSentProductIdRef.current = product.id;
    v.sendToUnity("LoadProduct", payload).catch(err =>
      console.warn("[RealtimeViewer] LoadProduct failed:", err)
    );
    // materialPicks intentionally omitted — LoadProduct already carried the
    // current picks. Subsequent picks-only updates flow through the effect
    // below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerReady, product?.id]);

  useEffect(() => {
    if (!viewerReady) return;
    const v = viewerRef.current;
    if (!v || !product) return;
    // Wait until LoadProduct has been sent for this product — otherwise we'd be
    // re-binding materials on a model Unity hasn't loaded yet.
    if (lastSentProductIdRef.current !== product.id) return;

    const bindings = buildBindings(product, materialPicks);
    v.sendToUnity("SetSurfaceMaterials", JSON.stringify({ bindings })).catch(err =>
      console.warn("[RealtimeViewer] SetSurfaceMaterials failed:", err)
    );
  }, [viewerReady, materialPicks, product]);

  // ── Push the camera clear colour ────────────────────────────────────
  // Unity uses this to paint the world background behind the model — match it
  // to the page surface so the realtime view reads as the same card as the
  // rendered media (otherwise the Unity skybox or whatever the scene defaults
  // to shows through and breaks the page composition).
  useEffect(() => {
    if (!viewerReady) return;
    const v = viewerRef.current;
    if (!v) return;
    v.sendToUnity("SetCameraClearColor", JSON.stringify({ hex: backgroundHex })).catch(err =>
      console.warn("[RealtimeViewer] SetCameraClearColor failed:", err)
    );
  }, [viewerReady, backgroundHex]);

  const handleUnityEvent = useCallback((eventType: string, payload: string) => {
    if (eventType === "ProductLoaded") {
      setUnityError(null);
      return;
    }
    if (eventType === "LoadError") {
      try {
        const parsed = JSON.parse(payload) as { error?: string };
        setUnityError(parsed.error ?? payload);
      } catch {
        setUnityError(payload);
      }
    }
  }, []);

  return (
    <div style={{ position: "fixed", inset: 0, width: "100vw", height: "100vh", background: backgroundHex }}>
      <style>{`
        :root {
          /* Default token values for the loader spinner + version chip. The
             ConfiguratorViewer CSS uses these via var() with fallbacks; setting
             them here lets us re-skin without touching the component. */
          --dptp-primary: #000;
          --dptp-muted: #6b7280;
          --dptp-soft-bg: ${backgroundHex};
        }
        html, body { margin: 0; height: 100%; background: ${backgroundHex}; }
        .dconfig-viewer { background: ${backgroundHex} !important; }
        .dconfig-viewer__loading { background: ${backgroundHex} !important; }
        .dconfig-viewer, .dconfig-viewer__canvas { width: 100% !important; height: 100% !important; }
        .dconfig-viewer__canvas { cursor: grab; }
        .dconfig-viewer__canvas:active { cursor: grabbing; }
      `}</style>
      {viewer ? (
        <ConfiguratorViewer
          ref={viewerRef}
          viewer={viewer}
          baseUrl={props.baseUrl}
          apiBaseUrl={props.apiBaseUrl}
          onReady={() => setViewerReady(true)}
          onUnityEvent={handleUnityEvent}
        />
      ) : (
        // No viewer yet → show the same loader chrome the ConfiguratorViewer
        // shows during Unity boot, so the iframe never goes blank. The classes
        // are styled by components/ConfiguratorViewer.css which is bundled
        // into the host's styles.css.
        <div className="dconfig-viewer">
          <div className="dconfig-viewer__loading">
            <div className="dconfig-viewer__loading-inner">
              <div className="dconfig-viewer__sk-grid" aria-label="Loading">
                {Array.from({ length: 9 }).map((_, i) => (
                  <span key={i} className="dconfig-viewer__sk-cube" />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {unityError && (
        <div style={{ position: "absolute", left: 12, right: 12, bottom: 12, padding: "8px 12px", background: "#b91c1ceb", color: "#fff", borderRadius: 6, fontSize: 12 }}>
          {unityError}
        </div>
      )}
    </div>
  );
}

export default RealtimeViewerTool;
